// src/pages/Home/Home.js
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Skeleton,
  Alert,
  Paper,
  TextField,
  InputAdornment
} from '@mui/material'
import { LocalOffer, Search } from '@mui/icons-material'
import axios from 'axios'
import { BoostSection } from './components/BoostSection'
import AtalhosSection from './components/AtalhosSection'
import ComoFuncionaSection from './components/ComoFuncionaSection'
import FaqSection from './components/FaqSection'
import ChamadaFinalSection from './components/ChamadaFinalSection'
import homeBanner from '../../assets/home.png'
import { propertiesAPI } from '../../services/api'
import { API_BASE_URL } from '../../config'
import { evento } from '../../analytics'

const Home = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Estados organizados
  const [allProperties, setAllProperties] = useState([])
  const [boostPlans, setBoostPlans] = useState([])
  const [superFeatured, setSuperFeatured] = useState([]) // Imóveis marcados pelo admin

  const [properties, setProperties] = useState({
    premium: [],
    pro: [],
    basic: [],
    regular: []
  })

  // Estado para busca do banner
  const [searchQuery, setSearchQuery] = useState('')

  // Função de busca
  const handleBannerSearch = () => {
    const termo = searchQuery.trim()
    evento('busca_realizada', { origem: 'home', termo: termo || '(vazio)' })
    if (termo) {
      navigate(`/imoveis?search=${encodeURIComponent(searchQuery)}`)
    } else {
      navigate('/imoveis')
    }
  }

  // Organizar propriedades por tipo de boost
  const getPropertiesByBoostType = useCallback(() => {
    if (!allProperties.length) {
      return { premium: [], pro: [], basic: [], regular: [] }
    }

    // Scores dos planos (fallback para valores padrão)
    const premiumPlan = boostPlans.find((p) => p.plan_type === 'premium')
    const proPlan = boostPlans.find((p) => p.plan_type === 'pro')
    const basicPlan = boostPlans.find((p) => p.plan_type === 'basic')

    const premiumScore = premiumPlan?.score_boost || 30
    const proScore = proPlan?.score_boost || 20
    const basicScore = basicPlan?.score_boost || 10

    const premiumBoost = []
    const proBoost = []
    const basicBoost = []
    const regular = []

    // Ordenar por boost_score decrescente
    const sorted = [...allProperties].sort(
      (a, b) => b.boost_score - a.boost_score
    )

    sorted.forEach((property) => {
      const score = property.boost_score

      if (score >= premiumScore) {
        if (premiumBoost.length < 6) premiumBoost.push(property)
      } else if (score >= proScore) {
        if (proBoost.length < 6) proBoost.push(property)
      } else if (score >= basicScore) {
        if (basicBoost.length < 6) basicBoost.push(property)
      } else {
        if (regular.length < 6) regular.push(property)
      }
    })

    return {
      premium: premiumBoost,
      pro: proBoost,
      basic: basicBoost,
      regular
    }
  }, [allProperties, boostPlans])

  useEffect(() => {
    fetchHomeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const organized = getPropertiesByBoostType()
    setProperties(organized)
  }, [allProperties, boostPlans, getPropertiesByBoostType])

  const fetchHomeData = async () => {
    try {
      setLoading(true)
      setError('')

      // Buscar imóveis Super Destaque (marcados pelo admin)
      const superFeaturedResponse = await axios.get(
        `${API_BASE_URL}/api/properties/super-featured/`
      )
      const superFeaturedData =
        superFeaturedResponse.data.results || superFeaturedResponse.data || []
      setSuperFeatured(superFeaturedData)
      console.log('⭐ Super Destaque (Admin):', superFeaturedData.length)

      // Buscar planos de boost para saber os score_boost de cada um
      const plansResponse = await axios.get(`${API_BASE_URL}/api/boost/plans/`)
      const plans = plansResponse.data.results || plansResponse.data || []
      setBoostPlans(plans)

      console.log('📦 Planos carregados:', plans)

      // Buscar todas as propriedades (usando propertiesAPI para incluir token de autenticação)
      const propertiesResponse = await propertiesAPI.list()
      const properties =
        propertiesResponse.data.results || propertiesResponse.data || []

      console.log('🏠 Propriedades carregadas:', properties.length)

      setAllProperties(properties)
    } catch (error) {
      console.error('❌ Erro ao carregar dados da home:', error)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      {/* Hero Section - Banner */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          // Mobile: altura fixa maior para acomodar busca | Desktop: aspect-ratio natural
          height: { xs: 340, sm: 380, md: 'auto' },
          aspectRatio: { xs: 'auto', md: '1934 / 459' },
          overflow: 'hidden'
        }}
      >
        {/* Imagem de fundo */}
        <Box
          component='img'
          src={homeBanner}
          alt='Banner'
          sx={{
            width: '100%',
            height: { xs: '100%', md: 'auto' },
            position: { xs: 'absolute', md: 'relative' },
            top: 0,
            left: 0,
            objectFit: { xs: 'cover', md: 'contain' },
            objectPosition: { xs: '90% center', md: 'center center' }
          }}
        />

        {/* Gradiente para legibilidade no mobile */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background:
              'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none'
          }}
        />

        {/* Posicionado, mas dentro do MESMO trilho de 1280px que todos os
            blocos abaixo. Antes era left:0 + px fixo de 80px: medido em 1920,
            o titulo comecava 272px a esquerda do resto da pagina. O trilho vem
            do Container, nao de um numero repetido aqui - se o valor mudar no
            tema, o heroi acompanha sozinho. */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '50%', sm: '50%', md: '18%' },
            transform: { xs: 'translateY(-50%)', md: 'none' },
            left: 0,
            right: 0
          }}
        >
          <Container maxWidth='xl'>
            {/* Coluna de texto: percentual do TRILHO, nao da tela, para nao
                atravessar a foto do banner no mobile. */}
            <Box sx={{ maxWidth: { xs: '75%', sm: '75%', md: '60%', lg: '58%' } }}>
              {/* Título */}
              <Typography
                variant='h1'
                sx={{
                  color: '#1a1a1a',
                  fontWeight: 700,
                  fontSize: {
                    xs: '1.6rem',
                    sm: '1.6rem',
                    md: '1.6rem',
                    lg: '2.5rem'
                  },
                  lineHeight: 1.2,
                  mb: { xs: 0.5, md: 2 }
                }}
              >
                Um marketplace simples para encontrar e anunciar seu imóvel.
              </Typography>

              {/* Barra de Busca */}
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 1.5, md: 1.5 },
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.97)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  alignItems: { xs: 'stretch', sm: 'center' }
                }}
              >
                <TextField
                  placeholder='Cidade, bairro ou palavra-chave'
                  size='small'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleBannerSearch()}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Search sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant='contained'
                  onClick={handleBannerSearch}
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 1,
                    bgcolor: '#2196F3',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#1976D2' }
                  }}
                >
                  Buscar
                </Button>
              </Paper>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Atalhos logo abaixo do heroi: e o primeiro lugar onde quem
          chegou sem saber o que procurar consegue entrar em algum
          lugar. Fica fora do !loading de proposito - nao depende da
          API e nao pode sumir enquanto a lista carrega. */}
      <AtalhosSection />

      {/* Erro */}
      {error && (
        <Container maxWidth='xl' sx={{ py: 4 }}>
          <Alert severity='error'>{error}</Alert>
        </Container>
      )}

      {/* IMÓVEIS MAIS PROCURADOS (Super Featured + Premium + Pro) */}
      {!loading &&
        (superFeatured.length > 0 ||
          properties.premium.length > 0 ||
          properties.pro.length > 0) && (
          <BoostSection
            title='Imóveis Mais Procurados'
            color='#FF7F27'
            properties={(() => {
              const combined = [...superFeatured]
              const ids = new Set(combined.map((p) => p.id))
              properties.premium.forEach((p) => {
                if (!ids.has(p.id)) {
                  combined.push(p)
                  ids.add(p.id)
                }
              })
              properties.pro.forEach((p) => {
                if (!ids.has(p.id)) {
                  combined.push(p)
                  ids.add(p.id)
                }
              })
              return combined.slice(0, 10)
            })()}
            seeMoreLink='/imoveis?highlight=true'
          />
        )}

      {/* Destaque Básico */}
      {!loading && properties.basic.length > 0 && (
        <BoostSection
          title='Imóveis em Destaque'
          icon={<LocalOffer sx={{ color: '#2196F3', fontSize: 32 }} />}
          color='#2196F3'
          properties={properties.basic}
          chipLabel='DESTAQUE'
          seeMoreLink='/imoveis?boost_type=basic'
        />
      )}

      {/* Novos Imóveis */}
      {!loading && properties.regular.length > 0 && (
        <BoostSection
          title='Novos Imóveis'
          color='#333'
          properties={properties.regular}
          seeMoreLink='/imoveis'
        />
      )}

      {/* Perguntas frequentes — conteudo tambem gerado em
          public/faq.html e em JSON-LD, para as IAs que nao
          executam JavaScript. Fonte unica: src/data/faq.js */}
      {/* Confianca antes das duvidas: a pessoa precisa saber o que e o
          UMARKT antes de o FAQ fazer sentido. */}
      <ComoFuncionaSection />

      <FaqSection />

      <ChamadaFinalSection />

      {/* Loading */}
      {loading && (
        <Container maxWidth='xl' sx={{ py: { xs: 1.5, md: 3 } }}>
          <Skeleton
            variant='text'
            height={50}
            sx={{ mb: { xs: 0, md: 3 }, width: { xs: '100%', md: '20%' } }}
          />
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}
                key={index}
              >
                <Skeleton
                  variant='rectangular'
                  height={200}
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton variant='text' sx={{ mt: 2 }} />
                <Skeleton variant='text' width='60%' />
              </Grid>
            ))}
          </Grid>
        </Container>
      )}
    </Box>
  )
}

export default Home
